<template>
  <div
    v-if="isAuthenticated && userName"
    :class="['user-info-card', { 'mobile-web': isMobileWeb, 'dark-theme': themeIsDark }]"
    @click="$emit('click')"
  >
    <IonAvatar class="user-avatar">
      <img :src="avatarUrl" :alt="$t('nav.userAvatar')" />
    </IonAvatar>
    <div class="user-details">
      <span class="user-name">{{ userName }}</span>
      <span class="user-email">{{ userEmail }}</span>
    </div>
  </div>
  <div v-else :class="['guest-info-card', { 'mobile-web': isMobileWeb }]">
    <IonIcon :icon="personCircleOutline" class="guest-icon" />
    <div class="guest-details">
      <span class="guest-title">{{ $t('common.guest') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IonAvatar, IonIcon } from '@ionic/vue';
import { computed } from 'vue';

import { personCircleOutline } from 'ionicons/icons';
import useTheming from '@/common/composables/useTheming';

const { themeMode } = useTheming();
const themeIsDark = computed(() => themeMode.value === 'dark');

defineProps<{
  isAuthenticated: boolean;
  userName: string | null;
  userEmail: string | null;
  avatarUrl: string;
  isMobileWeb?: boolean;
}>();

defineEmits<{
  click: [];
}>();
</script>

<style scoped>
.user-info-card,
.guest-info-card {
  background: var(--ion-card-background);

  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: var(--ion-box-shadow);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
}
.user-info-card:hover,
.guest-info-card:hover {
  border: 1px solid var(--ion-input-border-color);
}

.user-info-card.dark-theme {
  background: linear-gradient(135deg, #09090a 0%, #2d2d2d 100%);
}

.user-avatar {
  width: 80px;
  height: 80px;
  margin-bottom: 8px;
}

.guest-icon {
  font-size: 80px;
  color: var(--ion-text-color);
  margin-bottom: 8px;
}

.user-details,
.guest-details {
  display: flex;
  flex-direction: column;
  text-align: center;
}

.user-name,
.guest-title {
  font-weight: 600;
  font-size: 18px;
  color: var(--ion-text-color);
  margin-bottom: 4px;
}

.user-email {
  font-size: 14px;
  color: var(--ion-text-color);
  margin-bottom: 8px;
}

/* Mobile styles */
.user-info-card.mobile-web,
.guest-info-card.mobile-web {
  flex-direction: row;
  padding: 16px;
  gap: 12px;
  text-align: left;
  justify-content: flex-start;
}

.mobile-web .user-avatar {
  width: 50px;
  height: 50px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.mobile-web .guest-icon {
  font-size: 50px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.mobile-web .user-details,
.mobile-web .guest-details {
  text-align: left;
}

.mobile-web .user-name,
.mobile-web .guest-title {
  font-size: 16px;
}

.mobile-web .user-email {
  font-size: 12px;
  margin-bottom: 0;
}
</style>
